import { TranslateService } from '@ngx-translate/core';
import { PipeModel } from '@seahorse/common';
import {
  BaseFieldRule,
  CustomDataBaseModel,
  DisplayFieldConfiguration,
  FieldDefinitionModel,
  FieldMode,
  FieldType,
  ObjectDefinitionModel,
} from '@seahorse/domain';
import {
  BadgeModel,
  ProgressColorMap,
  ProgressDisplayFormat,
  ProgressDisplayType,
} from '@seahorse/ui';
import { ColumnModel } from '../table/models/table.model';

export interface DataField<TData = any> {
  key: ItemKey<TData>;
  name: string;
  type: FieldType;
  order?: number;
  pipeModels?: PipeModel[];
  badge?: Record<string, BadgeModel>;
  appendText?: string;
  displayFields?: string[];
  showCopyButton?: boolean;
  mode?: FieldMode;
  definition?: FieldDefinitionModel;
  hidden?: boolean;
  progressConfig?: {
    colorMap: ProgressColorMap[];
    format: ProgressDisplayFormat;
    type: ProgressDisplayType;
  };
}

export abstract class DataField {
  static fromFieldDefinition(
    field: FieldDefinitionModel,
    useFieldSort = true,
    config?: DisplayFieldConfiguration
  ): DataField {
    const pipeModels: PipeModel[] = [];
    const badge = {};
    const mode = FieldDefinitionModel.getAdditionalData(field)?.mode;

    if (field.formatting && field.formatting['decimalPipe']) {
      pipeModels.push(
        new PipeModel('decimalPipe', {
          format: field.formatting['decimalPipe'],
        })
      );
    }

    if (
      [FieldType.LinkedObject, FieldType.LinkedObjects].includes(
        field.fieldType
      )
    ) {
      pipeModels.push(
        new PipeModel('tempLinkedObjects', { systemCode: field.systemCode })
      );
    } else if (field.fieldType === FieldType.List) {
      pipeModels.push(
        new PipeModel('tempCustomListDisplay', {
          items: field.fieldRule['items'],
        })
      );

      BaseFieldRule.list(field.fieldRule).items.forEach((item) => {
        if (item.color) {
          badge[item.key] = {
            color: item.color,
            mode: mode?.display,
          };
        }
      });
    }

    if (field.fieldCode === 'user') {
      pipeModels.push(new PipeModel('shUserDisplay'));
    }

    if (field.systemCode === '__DataObjectKey') {
      pipeModels.push(new PipeModel('shCustomDataDefinitionType'));
    }

    return {
      key: field.systemCode,
      name: config?.columnName ?? field.name,
      type: field.fieldType,
      order: useFieldSort ? field.sortOrder : null,
      pipeModels: pipeModels.length ? pipeModels : undefined,
      badge: Object.keys(badge).length ? badge : undefined,
      appendText: field.fieldRule?.['valueDescriptor'],
      mode: mode,
      displayFields: config?.subDisplayFields?.split(','),
      definition: field,
    };
  }

  static fromObjectDefinition(
    objectDefinition: ObjectDefinitionModel,
    includeDefaultSystemField?: boolean,
    displayFieldsConfig?: DisplayFieldConfiguration[],
    displayFields?: string[],
    translateService?: TranslateService
  ): DataField[] {
    const fieldDefinitions = ObjectDefinitionModel.getAllFieldDefinitions(
      objectDefinition,
      includeDefaultSystemField,
      null,
      translateService
    );

    const templateCode = objectDefinition.templateCode
      ? JSON.parse(objectDefinition.templateCode)
      : null;

    let fieldsToDisplay = templateCode?.link?.mapping?.displayFields;

    if (displayFields?.length) {
      const filteredDisplayFields = displayFields.filter((f) => f && f.trim());
      if (filteredDisplayFields.length > 0) {
        fieldsToDisplay = filteredDisplayFields;
      }
    }

    if (displayFieldsConfig?.length) {
      displayFieldsConfig.forEach((config) => {
        const existingIndex = fieldsToDisplay?.findIndex(
          (x) => x === config.field
        );

        if (existingIndex !== -1) {
          fieldsToDisplay.splice(existingIndex, 1);
        }

        if (config.sortField && fieldsToDisplay) {
          fieldsToDisplay.splice(config.sortField - 1, 0, config.field);
        } else if (fieldsToDisplay) {
          fieldsToDisplay.push(config.field);
        }
      });
    }

    return fieldsToDisplay
      ?.map((x, index) => {
        const found = fieldDefinitions.find((y) => y.systemCode === x);
        if (!found) {
          return null;
        }

        const fieldConfig = displayFieldsConfig?.find(
          (y) => y.field === found.systemCode
        );

        // clone the field definition to avoid changing the original object and set the order of the display fields
        const field = JSON.parse(JSON.stringify(found));
        field.sortOrder = index;
        return DataField.fromFieldDefinition(field, false, fieldConfig);
      })
      .filter((x) => x !== null);
  }

  static toColumnModel<TData>(
    field: DataField<TData>
  ): ColumnModel<CustomDataBaseModel> {
    return {
      key: field.key as keyof CustomDataBaseModel & string,
      fieldType: field.type,
      name: field.name,
      definition: field.definition,
      displayFields: field.displayFields,
      order: field.order,
      appendText: field.appendText,
      pipes: field.pipeModels,
      showCopyButton: field.definition?.additionalDataModel?.showCopyButton,
      hidden: field.hidden,
      progressConfig: field.progressConfig,
    } as ColumnModel<CustomDataBaseModel>;
  }
}

export type ItemKey<T> = keyof T;
